import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Button } from '@abyss/web/ui/Button';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Grid } from '@abyss/web/ui/Grid';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { getLanguage } from '../../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../hooks/useNotAvailableUnknownText';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { useSuppressNiceMembers } from '../../../hooks/useSuppressNiceMembers';
import { ProviderLocation } from '../../../models/ProviderDetails';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { getUiMessageFromLagoon } from '../../../utils/common';
import { returnSuppressFlag } from '../../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import { shortenZipCode } from '../../../utils/providerDetails.utils';
import { capitalizeEachWord } from '../../../utils/providerNames.utils';
import { Constants, ReverseCoverageTypesCodes } from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import {
  infoIconButtonMobile,
  landingDrawerIcon,
  mobileOnly,
  phoneOnly,
} from '../../ConstantsStyles';
import { LinkWithTracking } from '../../LinkWithTracking/LinkWithTracking';
import {
  convertCoverageType,
  convertProviderTypeToAdobeType,
  handleLinkAndModalTrack,
} from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import { resolvePremiumCareDisclaimer } from '../../Utils/premiumCareDisclaimer';
import { ListItem } from '../About/CollapseProviderDetailsSection/ListCulturalCompetencies';
import {
  ContentText,
  PopoverContentContainer,
  PopoverIcon,
  StackDiv,
  TabContainer,
  boardCertificationsDescriptionStyle,
  drawerStyle,
  headerText,
  premiumCareSection,
  premiumTextIconStyle,
  qualityText,
  stackDivStyle,
} from './Quality.styled';

type Props = {
  boardCertifications?: string[];
  coverageType?: string;
  providerName: string;
  providerType: string;
  selectedLocation: ProviderLocation;
  tabTitle: string;
  organizationCode?: string[];
};

export const Quality = ({
  boardCertifications,
  coverageType,
  providerName,
  providerType,
  selectedLocation,
  tabTitle,
}: Props) => {
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const { coverageType: widgetCoverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );
  const lagoonData = useLagoon('ui-messaging')();
  const mobileScreen = useMediaQuery(mobileOnly);
  const WidgetDesktopScreen = !useMediaQuery(phoneOnly) && isWidget;
  const phoneScreen = useMediaQuery(phoneOnly);
  const phoneScreenNoWidget = useMediaQuery(phoneOnly);
  const { address, distance, isPremiumCarePhysician } = selectedLocation || {};
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: tabTitle,
    sitesectionLevel1: Constants.ADOBE_TRACKING.DETAILS_SITESECTION1,
    sitesectionLevel2: `${convertProviderTypeToAdobeType(
      providerType
    )} details`,
    providerBlock: {
      type: convertCoverageType(coverageType),
      role:
        isPremiumCarePhysician ===
        Constants.PROVIDER_DETAILS.QUALITY_TAB.PREMIUM_CARE_YES
          ? Constants.PROVIDER_DETAILS.QUALITY_TAB.PREMIUM.toLowerCase()
          : '',
    },
  });
  useEffect(() => {
    if (providerName) adobePageTrackEvent();
  }, [providerName]);

  const { line: addressLine, city, state, postalCode } = address || {};
  const shortenedZip = shortenZipCode(postalCode);

  const locale = getLanguage().code;
  const { popoverContent: premiumCarePhysicianContent, url: navigationURL } =
    resolvePremiumCareDisclaimer({
      locale,
      lagoonData,
    });

  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );
  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();

  const [enableBoardCertifiedProvidersFF, enableBhBoardCertification] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_BOARD_CERTIFIED_PROVIDERS,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_BOARD_CERTIFIED_PROVIDERS_BH,
    ]);

  const isMed =
    coverageType === ReverseCoverageTypesCodes.MEDICAL ||
    widgetCoverageType === ReverseCoverageTypesCodes.MEDICAL;

  const isBh =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    widgetCoverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];

  const boardCertificationsSectionDescription = getUiMessageFromLagoon(
    ConstantsLagoon.UI_MESSAGING.BOARD_CERTIFICATIONS_SECTION_DESCRIPTION,
    lagoonData
  );

  const isGEHAV2 = shouldUseGehaV2Styling();

  const shouldShowNotAvailable =
    !boardCertifications?.length || (isBh && !enableBhBoardCertification);

  useEffect(() => {
    if (isOpen) {
      const linkName: string = Constants.PROVIDER_DETAILS.QUALITY_TAB.PREMIUM;
      const linkLocation: string = `body:${convertProviderTypeToAdobeType(
        providerType
      )} details`;
      const modalName: string = Constants.PROVIDER_DETAILS.QUALITY_TAB.PREMIUM;
      handleLinkAndModalTrack(linkName, linkLocation, modalName);
    }
  }, [isOpen]);

  const { providerLocations: notAvailableOrUnknownText } =
    useNotAvailableUnknownText();

  const premiumCarePopoverContent = (
    <PopoverContentContainer>
      <ContentText
        data-auto-testid="premium-care-physician-info-text"
        data-testid="premium-care-physician-info-text"
      >
        <Text
          color="#323334"
          fontWeight={mobileScreen ? '500' : '400'}
          size={mobileScreen ? '$md' : '14px'}
        >
          {premiumCarePhysicianContent}
        </Text>
      </ContentText>
      <LinkWithTracking
        analyticsInfo={{
          name: 'Get my premium care',
          location: `modal:${Constants.PROVIDER_DETAILS.QUALITY_TAB.PREMIUM}`,
          type: 'download',
          destinationUrl: navigationURL,
        }}
        data-auto-testid="premium-care-physician-popover"
        data-testid="premium-care-physician-popover"
        href={navigationURL}
      >
        <Text
          color="$interactive1"
          css={{
            margin: '4px 0 4px 0',
            '@screen < $sm': {
              margin: '8px 0 8px 0',
            },
          }}
          fontWeight="700"
          size="$md"
        >
          {t('PROVIDER_DETAILS.QUALITY_TAB.POPOVER.POPOVER_LINK_TEXT')}
        </Text>
      </LinkWithTracking>
    </PopoverContentContainer>
  );

  const boardCertificationsList = (boardCertifications, withListWrapper) =>
    boardCertifications?.map((certification) =>
      withListWrapper ? (
        <li
          data-testid="board-certification-list"
          key={`certification-${certification}`}
        >
          {boardCertificationItem(certification)}
        </li>
      ) : (
        boardCertificationItem(certification)
      )
    );

  const boardCertificationItem = (certification) => (
    <Text
      css={qualityText(mobileScreen, isGEHAV2)}
      data-testid="board-certifications-list"
      key={`certification-${certification}`}
    >
      {capitalizeEachWord(certification)}
    </Text>
  );
  return (
    <TabContainer
      data-testid="provider-quality-tab"
      key={Constants.PROVIDER_DETAILS.QUALITY}
    >
      <Layout.Stack alignItems="center" grow space={-8}>
        {isPremiumCarePhysician ===
          Constants.PROVIDER_DETAILS.QUALITY_TAB.PREMIUM_CARE_YES &&
          !(suppressPremiumCare || suppressPremiumCareForNiceMembers) && (
            <StackDiv css={stackDivStyle(mobileScreen, WidgetDesktopScreen)}>
              <Layout.Group css={premiumCareSection(phoneScreen)} space={0}>
                <IconMaterial
                  color="$uhcPrimary1"
                  data-testid="premium-care-physician-icon"
                  icon="favorite"
                  size={mobileScreen ? '20' : '24'}
                />
                <IconMaterial
                  color="$uhcPrimary1"
                  css={premiumTextIconStyle(mobileScreen)}
                  data-testid="premium-care-physician-icon"
                  icon="favorite"
                  size={mobileScreen ? '20' : '24'}
                />
                <Heading
                  css={headerText(mobileScreen)}
                  data-testid="premium-care-physician-heading"
                  display="h6"
                  offset={1}
                >
                  {t('PROVIDER_DETAILS.QUALITY_TAB.PREMIUM')}
                </Heading>
                <Drawer
                  css={landingDrawerIcon}
                  isOpen={isDrawerOpen}
                  onClose={() => setIsDrawerOpen(false)}
                  position="bottom"
                  size="200"
                  title={t('PROVIDER_DETAILS.QUALITY_TAB.PREMIUM')}
                >
                  {premiumCarePopoverContent}
                </Drawer>
                {phoneScreenNoWidget ? (
                  <Button
                    aria-label={t(
                      'ACCESSIBILITY_LABELS.INFORMATION_ABOUT_PREMIUM_PRIMARY_PHYSICIAN'
                    )}
                    css={infoIconButtonMobile}
                    data-auto-testid="premium-care-physician-drawer"
                    data-testid="premium-care-physician-drawer"
                    onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                    variant="ghost"
                  >
                    <IconMaterial
                      css={drawerStyle}
                      icon="info"
                      size={16}
                      variant="outlined"
                    />
                  </Button>
                ) : (
                  <Popover
                    aria-label={t(
                      'ACCESSIBILITY_LABELS.INFORMATION_ABOUT_PREMIUM_PRIMARY_PHYSICIAN'
                    )}
                    content={premiumCarePopoverContent}
                    data-testid="premium-care-physician-info"
                    onOpenChange={() => setIsOpen(!isOpen)}
                    open={isOpen}
                    title={t('PROVIDER_DETAILS.QUALITY_TAB.PREMIUM') as string}
                    width={363}
                  >
                    <PopoverIcon
                      icon="info"
                      size={mobileScreen ? '16' : '20'}
                      variant="outlined"
                    />
                  </Popover>
                )}
              </Layout.Group>
              <Layout.Stack alignItems="left" space={0}>
                <Text css={qualityText(mobileScreen, isGEHAV2)} size="$md">
                  {addressLine?.join(', ')}
                </Text>
                <Text css={qualityText(mobileScreen, isGEHAV2)}>
                  {city}, {state} {shortenedZip} ({distance} {t('miles')})
                </Text>
              </Layout.Stack>
            </StackDiv>
          )}

        <StackDiv css={stackDivStyle(mobileScreen, WidgetDesktopScreen, true)}>
          <Grid space={0}>
            <Grid.Col span={{ xs: '100%', sm: '100%', md: '70%' }}>
              <Layout.Stack alignItems="left" space={12}>
                <Heading
                  css={headerText(mobileScreen)}
                  data-testid="board-certification-title"
                  display="h4"
                  offset={1}
                >
                  {t('PROVIDER_DETAILS.QUALITY_TAB.BOARD_CERTIFICATIONS_TITLE')}
                </Heading>
                {shouldShowNotAvailable ? (
                  <Text
                    css={qualityText(mobileScreen, isGEHAV2)}
                    data-testid="board-certifications-list"
                  >
                    {notAvailableOrUnknownText}
                  </Text>
                ) : (
                  <>
                    {isMed && enableBoardCertifiedProvidersFF ? (
                      <>
                        <Text
                          css={boardCertificationsDescriptionStyle(
                            mobileScreen,
                            isGEHAV2
                          )}
                          data-testid="board-certification-description"
                        >
                          {boardCertificationsSectionDescription}
                        </Text>
                        <ListItem>
                          {boardCertificationsList(boardCertifications, true)}
                        </ListItem>
                      </>
                    ) : (
                      isMed &&
                      boardCertificationsList(boardCertifications, false)
                    )}

                    {isBh && enableBhBoardCertification && (
                      <>
                        <Text
                          css={boardCertificationsDescriptionStyle(
                            mobileScreen,
                            isGEHAV2
                          )}
                          data-testid="board-certification-description"
                        >
                          {boardCertificationsSectionDescription}
                        </Text>
                        <ListItem>
                          {boardCertificationsList(boardCertifications, true)}
                        </ListItem>
                      </>
                    )}
                  </>
                )}
              </Layout.Stack>
            </Grid.Col>
          </Grid>
        </StackDiv>
      </Layout.Stack>
    </TabContainer>
  );
};
